import React, { useState, useEffect, useRef } from "react";
import Modal from "./Modal";
import { Baby, Building, Check, CheckCheck, ChevronDown, Copy, Dot, Frown, Meh, Minus, Plus, RefreshCw, Smile, SmilePlus, StickyNote } from "lucide-react";
import { Person } from "@mui/icons-material";
import { Checkbox, Contact, Dropdown, MultiFile, Text, formatDate } from "./FieldComponents";
import { ContactFieldPresets } from "./ContactFieldPresets";
import { useNavigate } from 'react-router-dom';
import { getContactInitialsClassName } from '../utils/colorUtils';
import '../styles/WeatherEvents.css';
import { EntityHoverPreview } from './EntityHoverPreview';

export const EditDetail = ({ setEditContact, contactData, fetchContacts, setError, setCreateContact, caseContact = false }) => {
    const navigate = useNavigate();
    const [contactInformation, setContactInformation] = useState({
        id: contactData.id,
        first_name: contactData.first_name || '',
        middle_name: contactData.middle_name || '',
        last_name: contactData.last_name || '',
        full_name: contactData.full_name || '',
        nickname: contactData.nickname || '',
        prefix: contactData.prefix || '',
        suffix: contactData.suffix || '',
        company_name: contactData.company_name || '',
        job_title: contactData.job_title || '',
        department: contactData.department || '',
        date_of_birth: contactData.date_of_birth || '',
        date_of_death: contactData.date_of_death || '',
        is_company: contactData.is_company === 1 || contactData.is_company === "1",
        nature: contactData.nature || null,
        nature_description: contactData.nature_description || '',
        contact_type: contactData.contact_type || '',
        socialMedia: contactData.socialMedia || [],
        gender: contactData.gender || '',
        pronouns: contactData.pronouns || '',
        language: contactData.language || '',
        emergencyContact: contactData.emergencyContact || null,
        ssn: contactData.ssn || '',
        dl: contactData.dl || '',
        dlp: contactData.dlp || [],
        education: contactData.education || '',
        additional_notes: contactData.additional_notes || '',
        opportunity: contactData.opportunity || 0
    });
    const [currentPage, setCurrentPage] = useState(0);
    const [deleteContact, setDeleteContact] = useState(false);
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePicturePreviewUrl, setProfilePicturePreviewUrl] = useState(null);
    const [removeProfilePicture, setRemoveProfilePicture] = useState(false);
    const [phones, setPhones] = useState([]);
    const [emails, setEmails] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [contactTypes, setContactTypes] = useState([]);
    const [relatedCases, setRelatedCases] = useState([]);
    const [copiedAddressIndex, setCopiedAddressIndex] = useState(null);
    const [showNoteFor, setShowNoteFor] = useState(null);
    const [isLead, setIsLead] = useState(false);
    const [showNatureModal, setShowNatureModal] = useState(false);
    const noteTextareaRef = useRef(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [draggedType, setDraggedType] = useState(null);
    const [contactPresets, setContactPresets] = useState([]);
    const [presetFieldMap, setPresetFieldMap] = useState({});
    const [selectedWeatherAddressIndex, setSelectedWeatherAddressIndex] = useState('');
    const [weatherEvents, setWeatherEvents] = useState([]);
    const [weatherError, setWeatherError] = useState('');
    const [weatherAddress, setWeatherAddress] = useState(null);
    const [weatherCoordinates, setWeatherCoordinates] = useState(null);
    const [isWeatherLoading, setIsWeatherLoading] = useState(false);
    const [showWeatherRefreshCheck, setShowWeatherRefreshCheck] = useState(false);

    useEffect(() => {
        console.log(contactInformation, contactData);
    }, [contactInformation]);

    useEffect(() => {
        if (!profilePicture || typeof profilePicture === 'string' || removeProfilePicture) {
            setProfilePicturePreviewUrl(null);
            return;
        }

        const nextPreviewUrl = URL.createObjectURL(profilePicture);
        setProfilePicturePreviewUrl(nextPreviewUrl);

        return () => {
            URL.revokeObjectURL(nextPreviewUrl);
        };
    }, [profilePicture, removeProfilePicture]);

    const formatAge = (birthDate) => {
        if (!birthDate) return '';
        
        const birth = new Date(birthDate);
        const today = new Date();
        
        let years = today.getFullYear() - birth.getFullYear();
        let months = today.getMonth() - birth.getMonth();
        let days = today.getDate() - birth.getDate();
        
        // Adjust for negative days
        if (days < 0) {
            months--;
            const lastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
            days += lastMonth.getDate();
        }
        
        // Adjust for negative months
        if (months < 0) {
            years--;
            months += 12;
        }
        
        const parts = [];
        if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
        if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
        if (days > 0 && years === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`); // Only show days if less than a year
        
        return parts.length > 0 ? ` (${parts.join(', ')} old)` : '';
    };

    const fetchIsLead = async (contactId) => {
        try {
            const response = await fetch(`https://api.casedb.co/lead.php?contact_id=${contactId}`);
            const data = await response.json();
            if (Number(data.lead_count) > 0) { 
                setIsLead(true);
            } else {
                setIsLead(false);
            }
        } catch (error) {
            console.error("Error checking if contact is a lead:", error);
            setIsLead(false);
        }
    };

    useEffect(() => {
        if (contactData) {
            setContactInformation({
                id: contactData.id,
                first_name: contactData.first_name || '',
                middle_name: contactData.middle_name || '',
                last_name: contactData.last_name || '',
                full_name: contactData.full_name || '',
                nickname: contactData.nickname || '',
                prefix: contactData.prefix || '',
                suffix: contactData.suffix || '',
                company_name: contactData.company_name || '',
                job_title: contactData.job_title || '',
                department: contactData.department || '',
                date_of_birth: contactData.date_of_birth || '',
                date_of_death: contactData.date_of_death || '',
                is_company: contactData.is_company === 1 || contactData.is_company === "1",
                nature: contactData.nature || null,
                nature_description: contactData.nature_description || '',
                contact_type: contactData.contact_type || '',
                socialMedia: contactData.socialMedia || [],
                gender: contactData.gender || '',
                pronouns: contactData.pronouns || '',
                language: contactData.language || '',
                emergencyContact: contactData.emergencyContact || null,
                ssn: contactData.ssn || '',
                dl: contactData.dl || '',
                dlp: contactData.dlp || [],
                education: contactData.education || '',
                additional_notes: contactData.additional_notes || '',
                opportunity: contactData.opportunity || 0,
                disregard: contactData.disregard || 0,
                disregard_reason: contactData.disregard_reason || ''
            });
    
            setPhones(
                Array.isArray(contactData?.phones) && contactData?.phones?.length > 0
                    ? contactData?.phones
                    : contactData?.details?.phone ? contactData?.details?.phone : [{ type: "mobile", number: "" }]
            );
    
            setEmails(
                Array.isArray(contactData?.emails) && contactData?.emails?.length > 0
                    ? contactData?.emails
                    : contactData?.details?.email ? contactData?.details?.email : [{ type: "personal", email: "" }]
            );
    
            setAddresses(
                Array.isArray(contactData?.addresses) && contactData?.addresses?.length > 0
                    ? contactData?.addresses
                    : contactData?.details?.address ? contactData?.details?.address : [{ type: "home", line1: "", line2: "", city: "", state: "", postal_code: "" }]
            );
    
            setProfilePicture(contactData.profile_picture ? `https://api.casedb.co/${contactData.profile_picture}` : null);
        }
    }, [contactTypes]);

    // Focus textarea when note section is opened
    useEffect(() => {
        if (showNoteFor && noteTextareaRef.current) {
            noteTextareaRef.current.focus();
        }
    }, [showNoteFor]);

    const addPhone = () => setPhones([...phones, { type: "mobile", number: "", note: "" }]);
    const addEmail = () => setEmails([...emails, { type: "personal", email: "", note: "" }]);
    const addAddress = () => setAddresses([...addresses, { type: "home", line1: "", line2: "", city: "", state: "", postal_code: "", note: "" }]);

    // Drag and drop handlers
    const handleDragStart = (e, index, type) => {
        // Only allow drag from empty space, not from inputs, buttons, selects, or textareas
        const target = e.target;
        if (target.tagName === 'INPUT' || 
            target.tagName === 'BUTTON' || 
            target.tagName === 'SELECT' || 
            target.tagName === 'TEXTAREA' ||
            target.closest('button') ||
            target.closest('select')) {
            e.preventDefault();
            return;
        }

        setDraggedIndex(index);
        setDraggedType(type);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index);
        e.currentTarget.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        // Remove all drop zone highlights
        document.querySelectorAll('.drag-over').forEach(el => {
            el.classList.remove('drag-over');
        });
        setDraggedIndex(null);
        setDraggedType(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        // Add visual feedback
        const target = e.currentTarget;
        if (!target.classList.contains('drag-over')) {
            target.classList.add('drag-over');
        }
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove('drag-over');
    };

    const handleDrop = (e, dropIndex, type) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Remove highlight
        e.currentTarget.classList.remove('drag-over');
        
        if (draggedIndex === null || draggedType !== type || draggedIndex === dropIndex) {
            return;
        }

        // Reorder the array by swapping items
        if (type === 'phone') {
            const newPhones = [...phones];
            const draggedItem = newPhones[draggedIndex];
            newPhones[draggedIndex] = newPhones[dropIndex];
            newPhones[dropIndex] = draggedItem;
            setPhones(newPhones);
            console.log('Reordered phones:', newPhones);
        } else if (type === 'email') {
            const newEmails = [...emails];
            const draggedItem = newEmails[draggedIndex];
            newEmails[draggedIndex] = newEmails[dropIndex];
            newEmails[dropIndex] = draggedItem;
            setEmails(newEmails);
            console.log('Reordered emails:', newEmails);
        } else if (type === 'address') {
            const newAddresses = [...addresses];
            const draggedItem = newAddresses[draggedIndex];
            newAddresses[draggedIndex] = newAddresses[dropIndex];
            newAddresses[dropIndex] = draggedItem;
            setAddresses(newAddresses);
            console.log('Reordered addresses:', newAddresses);
        }
    };

    const handleProfilePictureChange = (e) => {
        setProfilePicture(e.target.files[0]);
        setRemoveProfilePicture(false); // Clear removal flag if new picture is selected
    };

    const handleRemoveProfilePicture = () => {
        setProfilePicture(null);
        setRemoveProfilePicture(true);
        // Clear the file input
        const fileInput = document.getElementById('profilePictureInput');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const formatFullAddress = (address) => {
        if (!address?.line1 || !address?.state) {
            return;
        }

        console.log(address.line1, address.state)

        const parts = [];
        if (address.line1) parts.push(address.line1);
        if (address.line2) parts.push(address.line2);
        if (address.city) parts.push(address.city);
        
        // Combine state and postal code without comma between them
        const stateZip = [];
        if (address.state) stateZip.push(address.state);
        if (address.postal_code) stateZip.push(address.postal_code);
        if (stateZip.length > 0) parts.push(stateZip.join(' '));
        
        return parts.join(', ');
    };

    const formatWeatherAddress = (address) => {
        if (!address) {
            return '';
        }

        return [address.line1, address.line2, address.city, address.state, address.postal_code]
            .filter(Boolean)
            .join(', ');
    };

    const getWeatherAddressOptions = () => addresses
        .map((address, index) => ({
            address,
            index,
            label: formatFullAddress(address) || formatWeatherAddress(address) || `Address ${index + 1}`
        }))
        .filter(({ address }) => address?.line1?.trim() && address?.city?.trim() && address?.state?.trim());

    const formatWeatherEventDate = (eventDate) => {
        if (!eventDate) {
            return '-';
        }

        const [year, month, day] = eventDate.split('-').map(Number);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return `${monthNames[month - 1] || ''} ${String(day || '').padStart(2, '0')}, ${year || ''}`.trim();
    };

    const getWeatherEventType = (event) => {
        const types = Array.isArray(event?.types) ? event.types : [];
        if (types[0] === 'METEOROLOGIST_WIND_SPEED') {
            return 'Wind';
        }
        if (types[0] === 'ALGORITHM_HAIL_SIZE' || types[0] === 'METEOROLOGIST_HAIL_SIZE') {
            return 'Hail';
        }
        return types.length > 0 ? types.join(', ') : '-';
    };

    const getWeatherMapAddress = () => formatWeatherAddress(weatherAddress || addresses[Number(selectedWeatherAddressIndex)]);

    useEffect(() => {
        const options = getWeatherAddressOptions();
        if (options.length === 0) {
            setSelectedWeatherAddressIndex('');
            return;
        }

        if (!options.some(({ index }) => String(index) === String(selectedWeatherAddressIndex))) {
            setSelectedWeatherAddressIndex(String(options[0].index));
        }
    }, [addresses, selectedWeatherAddressIndex]);

    const fetchContactWeatherEvents = async (refresh = false, addressIndex = selectedWeatherAddressIndex) => {
        const address = addresses[Number(addressIndex)];
        if (!address?.line1 || !address?.city || !address?.state) {
            setWeatherError('Select an address with street, city, and state.');
            setWeatherEvents([]);
            return;
        }

        setIsWeatherLoading(true);
        setWeatherError('');

        try {
            const response = await fetch(`https://api.casedb.co/hailtrace-report.php?mode=address&refresh=${refresh}`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    mode: 'address',
                    contact_id: contactInformation.id,
                    address
                })
            });
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to load weather events');
            }

            setWeatherEvents(data?.hailtrace?.data?.filterWeatherEvents?.results || []);
            setWeatherAddress(data.address || address);
            setWeatherCoordinates(data.coordinates || null);
            setShowWeatherRefreshCheck(true);
            setTimeout(() => setShowWeatherRefreshCheck(false), 300);
        } catch (error) {
            setWeatherEvents([]);
            setWeatherAddress(null);
            setWeatherCoordinates(null);
            setWeatherError(error.message || 'Failed to load weather events');
        } finally {
            setIsWeatherLoading(false);
        }
    };

    useEffect(() => {
        if (currentPage !== 3 || selectedWeatherAddressIndex === '' || isWeatherLoading || weatherEvents.length > 0 || weatherError) {
            return;
        }

        fetchContactWeatherEvents(false, selectedWeatherAddressIndex);
    }, [currentPage, selectedWeatherAddressIndex]);

    const copyAddressToClipboard = async (address, index) => {
        const fullAddress = formatFullAddress(address);
        try {
            await navigator.clipboard.writeText(fullAddress);
            // Set the copied state for this specific address
            setCopiedAddressIndex(index);
            // Reset after 3 seconds
            setTimeout(() => {
                setCopiedAddressIndex(null);
            }, 3000);
            console.log('Address copied to clipboard:', fullAddress);
        } catch (err) {
            console.error('Failed to copy address:', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = fullAddress;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            // Set the copied state even for fallback
            setCopiedAddressIndex(index);
            setTimeout(() => {
                setCopiedAddressIndex(null);
            }, 3000);
        }
    };

    const deleteContactHandler = async () => {
        const response = await fetch(`https://api.casedb.co/update-contact.php?id=${Number(contactData.id)}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            }
        });
        const data = await response.json();

        if (data.success) {
            fetchContacts();
            setError({ message: 'Contact deleted successfully.', type: 'success' });
        } else if (data.message === 'Cannot delete contact: it is linked to a user or lead record.') {
            setError({ message: 'Cannot delete contact: it is linked to a user or lead record.', type: 'error' });
        } else {
            console.error("Error deleting contact:", response.statusText);
        }
        
        setEditContact(false);
        setDeleteContact(false);
    };

    const updateContactHandler = async () => {
        const formData = new FormData();
    
        const contactInformationToSave = {
            ...contactInformation,
            contact_type: contactInformation.contact_type,
            // Ensure JSON fields are properly stringified
            socialMedia: JSON.stringify(contactInformation.socialMedia || []),
            dlp: JSON.stringify(contactInformation.dlp || [])
        };
    
        Object.entries(contactInformationToSave).forEach(([key, value]) => {
            // Convert empty strings to null, but keep actual values
            const processedValue = (value === "" || value === "null") ? null : value;
            formData.append(key, processedValue);
        });
    
        formData.append('id', contactData.id);
    
        const cleanDetails = (items, fields) => {
            return items.map(item => {
                const cleaned = {};
                // Preserve the id field if it exists
                if (item.id) {
                    cleaned.id = item.id;
                }
                fields.forEach(field => {
                    cleaned[field] = item[field] === "" ? null : item[field];
                });
                return cleaned;
            });
        };

        const cleanedPhones = cleanDetails(phones, ["type", "number", "note"]);
        const cleanedEmails = cleanDetails(emails, ["type", "email", "note"]);
        const cleanedAddresses = cleanDetails(addresses, ["type", "line1", "line2", "city", "state", "postal_code", "note"]);        console.log('Saving contact details in order:', {
            phones: cleanedPhones,
            emails: cleanedEmails,
            addresses: cleanedAddresses
        });

        formData.append('phones', JSON.stringify(cleanedPhones));
        formData.append('emails', JSON.stringify(cleanedEmails));
        formData.append('addresses', JSON.stringify(cleanedAddresses));

        const presetPayload = contactPresets
            .filter(preset => preset.source_field_id && preset.target_field_id)
            .map((preset) => ({
                source_field_id: preset.source_field_id,
                target_field_id: preset.target_field_id,
                target_value: preset.target_value
            }));
        formData.append('contact_field_presets', JSON.stringify(presetPayload));

        if (removeProfilePicture) {
            formData.append('remove_profile_picture', 'true');
        } else if (profilePicture) {
            formData.append('profile_picture', profilePicture);
        }

        try {
            const response = await fetch(`https://api.casedb.co/update-contact.php`, {
                method: "POST",
                body: formData
            });
    
            if (response.ok) {
                // If we removed the profile picture, update the contact information state
                if (removeProfilePicture) {
                    setContactInformation(prev => ({
                        ...prev,
                        profile_picture: null
                    }));
                    setRemoveProfilePicture(false);
                }
                
                // Refetch the updated contact data
                try {
                    const contactResponse = await fetch(`https://api.casedb.co/contacts.php?id=${contactData.id}`, { credentials: 'include' });
                    if (contactResponse.ok) {
                        const contactResult = await contactResponse.json();
                        console.log('Contact refetched after update:', contactResult);
                    }
                } catch (refetchError) {
                    console.error("Error refetching contact:", refetchError);
                }
                
                fetchContacts();
                setEditContact(false);
            } else {
                console.error("Error updating contact:", response.statusText);
            }
        } catch (error) {
            console.error("Error updating contact:", error);
        }
    };

    const createLeadHandler = async () => {
        try {
            const leadData = {
                intake_by: 12,
                contact_id: contactData.id,
                preferred_contact: 'email',
                case_type: 1,
                marketing_source: null,
                office: 1,
                assigned_to: 12,
                status: 1,
                summary: `Lead created from contact: ${contactData.full_name || contactData.first_name + ' ' + contactData.last_name}`,
                case_likelihood: 3,
                notes: 'Lead created from contact opportunity'
            };

            const response = await fetch('https://api.casedb.co/leads.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(leadData),
                credentials: 'include'
            });

            const result = await response.json();
            
            if (result.success) {
                // Navigate to the new lead page
                navigate(`/intake/${result.lead_id}`);
            } else {
                console.error('Error creating lead:', result.message);
                alert('Failed to create lead: ' + result.message);
            }
        } catch (error) {
            console.error('Error creating lead:', error);
            alert('Failed to create lead. Please try again.');
        }
    };

    const fetchRelatedCases = () => {
        fetch(`https://api.casedb.co/related_cases.php?contact_id=${contactInformation.id}`)
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    // Combine both contact_related_cases and manual_related_cases
                    const allCases = [
                        ...(data.contact_related_cases || []),
                        ...(data.manual_related_cases || [])
                    ];
                    setRelatedCases(allCases);
                } else {
                    console.error("Error fetching related cases:", data.message);
                    setRelatedCases([]);
                }
            })
            .catch(error => {
                console.error("Error fetching related cases:", error);
                setRelatedCases([]);
            });
    };

    const handleOpenNatureModal = () => {
        setShowNatureModal(true);
    };

    const handleSaveNature = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/contacts.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    contact_id: contactInformation.id,
                    nature: contactInformation.nature,
                    nature_description: contactInformation.nature_description
                }),
                credentials: 'include'
            });
            if (response.ok) {
                setShowNatureModal(false);
                if (fetchContacts) fetchContacts();
            }
        } catch (error) {
            console.error('Error saving nature:', error);
        }
    };

    const exportVCard = () => {
        // Build vCard 3.0 format
        let vcard = 'BEGIN:VCARD\n';
        vcard += 'VERSION:3.0\n';
        
        // Full name
        const fullName = contactInformation.full_name || `${contactInformation.first_name} ${contactInformation.last_name}`.trim();
        if (fullName) {
            vcard += `FN:${fullName}\n`;
        }
        
        // Structured name (Last;First;Middle;Prefix;Suffix)
        const lastName = contactInformation.last_name || '';
        const firstName = contactInformation.first_name || '';
        const middleName = contactInformation.middle_name || '';
        const prefix = contactInformation.prefix || '';
        const suffix = contactInformation.suffix || '';
        vcard += `N:${lastName};${firstName};${middleName};${prefix};${suffix}\n`;
        
        // Nickname
        if (contactInformation.nickname) {
            vcard += `NICKNAME:${contactInformation.nickname}\n`;
        }
        
        // Organization and title
        if (contactInformation.company_name) {
            vcard += `ORG:${contactInformation.company_name}\n`;
        }
        if (contactInformation.job_title) {
            vcard += `TITLE:${contactInformation.job_title}\n`;
        }
        
        // Phone numbers
        phones.forEach(phone => {
            if (phone.number) {
                const type = phone.type.toUpperCase();
                vcard += `TEL;TYPE=${type}:${phone.number}\n`;
            }
        });
        
        // Email addresses
        emails.forEach(email => {
            if (email.email) {
                const type = email.type.toUpperCase();
                vcard += `EMAIL;TYPE=${type}:${email.email}\n`;
            }
        });
        
        // Addresses
        addresses.forEach(address => {
            if (address.line1 || address.city || address.state || address.postal_code) {
                const type = address.type.toUpperCase();
                // ADR format: ;;street;city;state;zip;country
                vcard += `ADR;TYPE=${type}:;;${address.line1 || ''}${address.line2 ? ' ' + address.line2 : ''};${address.city || ''};${address.state || ''};${address.postal_code || ''};\n`;
            }
        });
        
        // Birthday
        if (contactInformation.date_of_birth) {
            const birthDate = new Date(contactInformation.date_of_birth);
            const year = birthDate.getFullYear();
            const month = String(birthDate.getMonth() + 1).padStart(2, '0');
            const day = String(birthDate.getDate()).padStart(2, '0');
            vcard += `BDAY:${year}-${month}-${day}\n`;
        }
        
        // Gender
        if (contactInformation.gender) {
            vcard += `X-GENDER:${contactInformation.gender}\n`;
        }
        
        // Notes
        if (contactInformation.additional_notes) {
            vcard += `NOTE:${contactInformation.additional_notes.replace(/\n/g, '\\n')}\n`;
        }
        
        vcard += 'END:VCARD';
        
        // Create blob and download
        const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fullName || 'contact'}.vcf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        const response = fetch("https://api.casedb.co/contacts.php?types=true", { credentials: 'include' });
        response.then(res => res.json()).then(data => {
            if (data.success) {
                setContactTypes(data.contact_types || []);
            } else {
                console.error("Error fetching contact types:", data.message);
            }
        }).catch(error => {
            console.error("Error fetching contact types:", error);
        });
    }, []);

    useEffect(() => {
        fetchIsLead(contactData.id);
    }, [contactData.id]);

    return (
        <Modal
            onClose={() => setEditContact(false)}
            title={
                    !deleteContact ? (
                        <>
                            {(() => {
                                const prefix = contactData?.prefix || contactInformation?.prefix || "";
                                if (!prefix) return "";

                                const trimmed = prefix.trimEnd();
                                // Add a period only if it doesn’t already end with one
                                const finalPrefix = trimmed.endsWith(".") ? trimmed : `${trimmed}.`;

                                return `${finalPrefix} `;
                            })()}
                            {contactData?.full_name}
                            <span className="subtext" style={{ userSelect: "text" }}>
                                {" "} - Edit Contact ({contactInformation?.id})
                            </span>
                            {!contactInformation.is_company &&
                            contactInformation?.date_of_birth &&
                            new Date(contactInformation.date_of_birth).getTime() >=
                                new Date(new Date().setFullYear(new Date().getFullYear() - 18)).getTime() && (
                                    <Baby size={24} style={{ verticalAlign: "middle" }} />
                                )
                            }
                            <div className='contact-nature' onClick={handleOpenNatureModal} style={{ cursor: 'pointer' }}>
                                {contactInformation?.nature === 1 ? <Smile size={18} className="contact-nature-icon well-natured" /> : contactInformation?.nature === 2 ? <Meh size={18} className="contact-nature-icon neutral-natured" /> : contactInformation?.nature === 3 ? <Frown size={18} className="contact-nature-icon ill-natured" /> : <SmilePlus size={18} className="contact-nature-icon" />}
                            </div>
                        </>
                    ) : (
                        <span>Delete Contact</span>
                    )
                }
            wider
            header={(
                <div className='modal-header-actions'>
                    {!deleteContact && (
                        <>
                            {!caseContact && <button className='action destroy' onClick={() => setDeleteContact(true)}>Delete</button>}
                            <button className='action quaternary' style={{ backgroundColor: 'var(--background-color)' }} onClick={exportVCard}>Export Contact</button>
                            {(contactInformation.opportunity !== undefined && !isLead ? contactInformation.opportunity : contactData?.opportunity) == 1 && (
                                <button className='action tertiary' style={{ backgroundColor: 'var(--background-color)' }} onClick={createLeadHandler}>Create Lead</button>
                            )}
                            <button className='action alt' onClick={() => setEditContact(false)}>Cancel</button>
                            <button className='action' onClick={updateContactHandler}>Save</button>
                        </>
                    )}
                </div>
            )}
            footer={
                deleteContact && !caseContact && (
                    <div className='modal-footer-actions'>
                        <button className='action alt' onClick={() => setDeleteContact(false)}>Cancel</button>
                        <button className='action destroy' onClick={() => {
                            deleteContactHandler();
                        }}>Delete</button>
                    </div>
                )
            }
        >
            <div className='modal-content-wrapper'>
                {!deleteContact ? (
                    <form style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', flexDirection: 'column', gap: '20px', width: '100%' }}>
                        <div className='modal-navigation'>
                            <span 
                                className={`${currentPage === 0 ? 'active' : ''}`} 
                                onClick={() => setCurrentPage(0)}
                            >
                                Contact Details
                            </span>
                            <span 
                                className={`${currentPage === 1 ? 'active' : ''}`} 
                                onClick={() => {
                                    setCurrentPage(1);
                                }}
                            >
                                Personal Details
                            </span>
                            <span 
                                className={`${currentPage === 2 ? 'active' : ''}`} 
                                onClick={() => {
                                    setCurrentPage(2);
                                    fetchRelatedCases();
                                }}
                            >
                                Associated Cases
                            </span>
                            <span 
                                className={`${currentPage === 3 ? 'active' : ''}`} 
                                onClick={() => setCurrentPage(3)}
                            >
                                Weather Events
                            </span>
                        </div>
                        {currentPage === 0 ? (
                            <>
                                <div className='sub-title'>
                                    <h4>Basic Information</h4>
                                    <div className='person-or-company'>
                                        <div
                                            className={`poc person${!contactInformation.is_company ? ' active' : ''}`}
                                            onClick={() => setContactInformation({ ...contactInformation, is_company: false })}
                                            title='Is the contact a person?'
                                        >
                                            <Person />
                                        </div>
                                        <div
                                            className={`poc company${contactInformation.is_company ? ' active' : ''}`}
                                            onClick={() => setContactInformation({ ...contactInformation, is_company: true })}
                                            title='Is the contact a company?'
                                        >
                                            <Building />
                                        </div>
                                    </div>
                                    <div className="contact-type">
                                        <Dropdown
                                            options={contactTypes}
                                            placeholder="Select Contact Type"
                                            onChange={(index) => {
                                                const selectedType = contactTypes[index] || '';
                                                setContactInformation({ ...contactInformation, contact_type: selectedType });
                                            }}
                                            value={contactTypes.indexOf(contactInformation.contact_type)}
                                        />
                                    </div>
                                    <Checkbox
                                        label="Is this an opportunity?"
                                        value={!caseContact ? contactInformation.opportunity : true}
                                        onChange={() => setContactInformation({ ...contactInformation, opportunity: contactInformation.opportunity == 1 ? 0 : 1 })}
                                    />
                                    {contactInformation.opportunity == 1 && !caseContact && (
                                        <div className={`action ${contactInformation.disregard == 0 ? "quaternary" : "quinary"}`} onClick={() => setContactInformation({ ...contactInformation, disregard: contactInformation.disregard == 1 ? 0 : 1 })}>
                                            {contactInformation.disregard == 1 ? "Disregarded" : "Disregard"}
                                        </div>
                                    )}
                                    {contactInformation.disregard == 1 && (
                                        <div className="form-group nm">
                                            <input type='text' value={contactInformation.disregard_reason} onChange={(e) => setContactInformation({ ...contactInformation, disregard_reason: e.target.value })} placeholder="Reason for disregarding" />
                                        </div>
                                    )}
                                </div>
                                <div className='contact-picture-container'>
                                    <div className="contact-picture form-box" onClick={() => document.getElementById('profilePictureInput').click()}>
                                        {profilePicture && !removeProfilePicture ? (
                                            typeof profilePicture === "string" ? 
                                            <img className='contact-picture-listed' src={profilePicture} alt="Contact" /> : 
                                            <img className='contact-picture-listed' src={profilePicturePreviewUrl || ''} alt="Contact" />
                                        ) : (
                                            <Person />
                                        )}
                                        <input
                                            type="file"
                                            id="profilePictureInput"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={handleProfilePictureChange}
                                        />
                                    </div>
                                    {profilePicture && !removeProfilePicture && <button className='action quinary' onClick={handleRemoveProfilePicture}>
                                        Remove Picture
                                    </button>}
                                </div>
                                <div className='contact-name-container'>
                                    {!contactInformation.is_company && (
                                        <>
                                            <div className='contact-name-info'>
                                                <div className="form-group nm fname name-fg">
                                                    <label className='subtext' htmlFor="first_name">First Name<span className='required'>*</span></label>
                                                    <input type="text" id="first_name" autoFocus value={contactInformation.first_name} onChange={(e) => setContactInformation({ ...contactInformation, first_name: e.target.value })} />
                                                </div>
                                                <div className="form-group nm mname name-fg">
                                                    <label className='subtext' htmlFor="middle_name">Middle Name</label>
                                                    <input type="text" id="middle_name" value={contactInformation.middle_name} onChange={(e) => setContactInformation({ ...contactInformation, middle_name: e.target.value })} />
                                                </div>
                                                <div className="form-group nm lname name-fg">
                                                    <label className='subtext' htmlFor="last_name">Last Name<span className='required'>*</span></label>
                                                    <input type="text" id="last_name" value={contactInformation.last_name} onChange={(e) => setContactInformation({ ...contactInformation, last_name: e.target.value })} />
                                                </div>
                                                <div className="form-group nm nickname">
                                                    <label className='subtext' htmlFor="nickname">Nickname</label>
                                                    <input type="text" id="nickname" value={contactInformation.nickname} onChange={(e) => setContactInformation({ ...contactInformation, nickname: e.target.value })} />
                                                </div>
                                                <div className="form-group nm prefix name-fg">
                                                    <label className='subtext' htmlFor="prefix">Prefix</label>
                                                    <input type="text" id="prefix" value={contactInformation.prefix} onChange={(e) => setContactInformation({ ...contactInformation, prefix: e.target.value })} />
                                                </div>
                                                <div className="form-group nm suffix name-fg">
                                                    <label className='subtext' htmlFor="suffix">Suffix</label>
                                                    <input type="text" id="suffix" value={contactInformation.suffix} onChange={(e) => setContactInformation({ ...contactInformation, suffix: e.target.value })} />
                                                </div>
                                            </div>
                                            <div className='divider horizontal'></div>
                                        </>
                                    )}
                                    <div className="form-group nm cname">
                                        <label className='subtext' htmlFor="company_name">Company Name</label>
                                        <input type="text" id="company_name" value={contactInformation.company_name} onChange={(e) => setContactInformation({ ...contactInformation, company_name: e.target.value })} />
                                    </div>
                                    <div className="form-group nm jtitle">
                                        <label className='subtext' htmlFor="job_title">Job Title</label>
                                        <input type="text" id="job_title" value={contactInformation.job_title} onChange={(e
                                        ) => setContactInformation({ ...contactInformation, job_title: e.target.value })} />
                                    </div>
                                    <div className="form-group nm dname">
                                        <label className='subtext' htmlFor="department">Department</label>
                                        <input type="text" id="department" value={contactInformation.department} onChange={(e) => setContactInformation({ ...contactInformation, department: e.target.value })} />
                                    </div>
                                    <div className="form-group nm dob">
                                        <label className='subtext' htmlFor="date_of_birth">{!contactInformation.is_company ? "Date of Birth" : "Creation Date"}{formatAge(contactInformation.date_of_birth)}</label>
                                        <input type="date" id="date_of_birth" value={contactInformation.date_of_birth} onChange={(e) => setContactInformation({ ...contactInformation, date_of_birth: e.target.value })} />
                                    </div>
                                    {!contactInformation.is_company && (
                                        <div className="form-group nm dod">
                                            <label className='subtext' htmlFor="date_of_death">Date of Death</label>
                                            <input type="date" id="date_of_death" value={contactInformation.date_of_death} onChange={(e) => setContactInformation({ ...contactInformation, date_of_death: e.target.value })} />
                                        </div>
                                    )}
                                    <div className='form-group filter-group nm pref-contact'>
                                        <label className='subtext' htmlFor='preferred_contact_method'>Preferred Contact Method</label>
                                        <div className="select-wrapper" style={{ width: '100%' }}>
                                            <select className='filter-select' id='preferred_contact_method' style={{ width: '100%' }}>
                                                <option value='' disabled>Select...</option>
                                                <option value='email'>Email</option>
                                                <option value='phone'>Phone</option>
                                                <option value='mail'>Mail</option>
                                            </select>
                                            <ChevronDown size={16} className="select-chevron" />
                                        </div>
                                    </div>
                                </div>
                                <div className='sub-title'>
                                    <h4>Contact Information</h4>
                                </div>
                                <div className='contact-information'>
                                    <div className='detail-container'>
                                        {phones.map((phone, index) => (
                                            <div 
                                                key={`phone-${index}`} 
                                                className='phone-number-container'
                                                draggable={true}
                                                onDragStart={(e) => handleDragStart(e, index, 'phone')}
                                                onDragEnd={handleDragEnd}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, index, 'phone')}
                                                style={{ cursor: 'grab', transition: 'all 0.2s ease' }}
                                            >
                                                <div className='form-group filter-group nm phone-type'>
                                                    <label className='subtext' htmlFor={`phone-type-${index}`}>Phone Type</label>
                                                    <div className="select-wrapper" style={{ width: '100%' }}>
                                                        <select className='filter-select' style={{ width: '100%' }} value={phone.type} onChange={(e) => {
                                                            const newPhones = [...phones];
                                                            newPhones[index].type = e.target.value;
                                                            setPhones(newPhones);
                                                        }}>
                                                            <option value="mobile">Mobile</option>
                                                            <option value="home">Home</option>
                                                            <option value="work">Work</option>
                                                            <option value="workmobile">Mobile (Work)</option>
                                                            <option value="fax">Fax</option>
                                                            <option value="other">Other</option>
                                                        </select>
                                                        <ChevronDown size={16} className="select-chevron" />
                                                    </div>
                                                </div>
                                                <div className='form-group nm phone-input'>
                                                    <label className='subtext' htmlFor={`phone-number-${index}`}>Number</label>
                                                    <input type="tel" id={`phone-number-${index}`} value={phone.number} onChange={(e) => {
                                                        const newPhones = [...phones];
                                                        newPhones[index].number = e.target.value;
                                                        setPhones(newPhones);
                                                    }} />
                                                </div>
                                                <button 
                                                    type="button" 
                                                    className={`form-box ${phone?.note?.length > 0 ? 'desc' : 'alt'}`}
                                                    title='Add/Edit phone notes'
                                                    onClick={() => setShowNoteFor(showNoteFor === `phone-${index}` ? null : `phone-${index}`)}
                                                >
                                                    <StickyNote size={14} color='var(--secondary-color)' />
                                                </button>
                                                {showNoteFor === `phone-${index}` && (
                                                    <div className='contact-note-editor'>
                                                        <textarea 
                                                            ref={noteTextareaRef}
                                                            placeholder="Phone notes..."
                                                            value={phone.note || ""} 
                                                            onChange={(e) => {
                                                                const newPhones = [...phones];
                                                                newPhones[index].note = e.target.value;
                                                                setPhones(newPhones);
                                                            }} 
                                                            onBlur={() => setShowNoteFor(null)}
                                                            rows="3"
                                                        />
                                                    </div>
                                                )}
                                                {index > 0 ? (
                                                    <button type="button" className='form-box alt' title='Remove this phone number' onClick={() => {
                                                        const newPhones = [...phones];
                                                        newPhones.splice(index, 1);
                                                        setPhones(newPhones);
                                                    }}><Minus size={14} /></button>
                                                ) : (
                                                    <div className='form-box alt' title='Add another phone number' onClick={addPhone}><Plus size={14} /></div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div className='divider horizontal'></div>
                                    <div className='detail-container'>
                                        {emails.map((email, index) => (
                                            <div 
                                                key={`email-${index}`} 
                                                className='email-container'
                                                draggable={true}
                                                onDragStart={(e) => handleDragStart(e, index, 'email')}
                                                onDragEnd={handleDragEnd}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                onDrop={(e) => handleDrop(e, index, 'email')}
                                                style={{ cursor: 'grab', transition: 'all 0.2s ease' }}
                                            >
                                                <div className='form-group nm email-type'>
                                                    <label className='subtext' htmlFor={`email-type-${index}`}>Email Type</label>
                                                    <select className='default-select' id={`email-type-${index}`} value={email.type} onChange={(e) => {
                                                        const newEmails = [...emails];
                                                        newEmails[index].type = e.target.value;
                                                        setEmails(newEmails);
                                                    }}>
                                                        <option value="personal">Personal</option>
                                                        <option value="work">Work</option>
                                                        <option value="other">Other</option>
                                                    </select>
                                                </div>
                                                <div className='form-group nm email-input'>
                                                    <label className='subtext' htmlFor={`email-address-${index}`}>Email</label>
                                                    <input type="email" id={`email-address-${index}`} value={email.email} onChange={(e) => {
                                                        const newEmails = [...emails];
                                                        newEmails[index].email = e.target.value;
                                                        setEmails(newEmails);
                                                    }} />
                                                </div>
                                                <button 
                                                    type="button" 
                                                    className={`form-box ${email?.note?.length > 0 ? 'desc' : 'alt'}`}
                                                    title='Add/Edit email notes'
                                                    onClick={() => setShowNoteFor(showNoteFor === `email-${index}` ? null : `email-${index}`)}
                                                >
                                                    <StickyNote size={14} color='var(--secondary-color)' />
                                                </button>
                                                {showNoteFor === `email-${index}` && (
                                                    <div className='contact-note-editor'>
                                                        <textarea 
                                                            ref={noteTextareaRef}
                                                            placeholder="Email notes..."
                                                            value={email.note || ""} 
                                                            onChange={(e) => {
                                                                const newEmails = [...emails];
                                                                newEmails[index].note = e.target.value;
                                                                setEmails(newEmails);
                                                            }} 
                                                            onBlur={() => setShowNoteFor(null)}
                                                            rows="3"
                                                        />
                                                    </div>
                                                )}
                                                {index > 0 ? (
                                                    <button type="button" className='form-box alt' title='Remove this email' onClick={() => {
                                                        const newEmails = [...emails];
                                                        newEmails.splice(index, 1);
                                                        setEmails(newEmails);
                                                    }}><Minus size={14} /></button>
                                                ) : (
                                                    <div className='form-box alt' title='Add another email' onClick={addEmail}><Plus size={14} /></div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    <div className='divider horizontal'></div>
                                    {addresses.map((address, index) => (
                                        <div 
                                            key={index} 
                                            className='address'
                                            draggable={true}
                                            onDragStart={(e) => handleDragStart(e, index, 'address')}
                                            onDragEnd={handleDragEnd}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, index, 'address')}
                                            style={{ cursor: 'grab', transition: 'all 0.2s ease' }}
                                        >
                                            <div className='address-heading'>
                                                <span className="subheading">
                                                    {formatFullAddress(address) || `Address ${index + 1}`}
                                                </span>
                                                {formatFullAddress(address) && (
                                                    <span
                                                        type="button"
                                                        className='copy-paste'
                                                        onClick={() => copyAddressToClipboard(address, index)}
                                                        title="Copy full address to clipboard"
                                                    >
                                                        {copiedAddressIndex === index ? (
                                                            <CheckCheck size={12} />
                                                        ) : (
                                                            <Copy size={12} />
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            <div className='form-group nm address-detail main-data'>
                                                <label className='subtext' htmlFor="address-type">Type</label>
                                                <select className='default-select' value={address.type} name='address-type' onChange={(e) => {
                                                    const updated = [...addresses];
                                                    updated[index].type = e.target.value;
                                                    setAddresses(updated);
                                                }}>
                                                    <option value="home">Home</option>
                                                    <option value="work">Work</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                            <div className='form-group nm address-detail main-data'>
                                                <label className='subtext'>Address Line 1</label>
                                                <input type="text" value={address.line1} onChange={(e) => {
                                                    const updated = [...addresses];
                                                    updated[index].line1 = e.target.value;
                                                    setAddresses(updated);
                                                }} />
                                            </div>
                                            <div className='form-group nm address-detail main-data'>
                                                <label className='subtext'>Address Line 2</label>
                                                <input type="text" value={address.line2} onChange={(e) => {
                                                    const updated = [...addresses];
                                                    updated[index].line2 = e.target.value;
                                                    setAddresses(updated);
                                                }} />
                                            </div>
                                            <div className='form-group nm address-detail main-data'>
                                                <label className='subtext'>City</label>
                                                <input type="text" value={address.city} onChange={(e) => {
                                                    const updated = [...addresses];
                                                    updated[index].city = e.target.value;
                                                    setAddresses(updated);
                                                }} />
                                            </div>
                                            <div className='form-group nm address-detail'>
                                                <label className='subtext'>State</label>
                                                <input type="text" value={address.state} onChange={(e) => {
                                                    const updated = [...addresses];
                                                    updated[index].state = e.target.value;
                                                    setAddresses(updated);
                                                }} />
                                            </div>
                                            <div className='form-group nm address-detail postal'>
                                                <label className='subtext'>Postal Code</label>
                                                <input type="text" value={address.postal_code} onChange={(e) => {
                                                    const updated = [...addresses];
                                                    updated[index].postal_code = e.target.value;
                                                    setAddresses(updated);
                                                }} />
                                            </div>
                                            <div className='detail-box-container'>
                                                <button 
                                                    type="button" 
                                                    className={`form-box ${address?.note?.length > 0 ? 'desc' : 'alt'}`}
                                                    title='Add/Edit address notes'
                                                    style={{ color: address.note ? 'var(--fill)' : 'inherit' }}
                                                    onClick={() => setShowNoteFor(showNoteFor === `address-${index}` ? null : `address-${index}`)}
                                                >
                                                    <StickyNote size={14} color='var(--secondary-color)' />
                                                </button>
                                                {index > 0 ? (
                                                    <button type="button" className='form-box alt' title='Remove this address' onClick={() => {
                                                        const newAddresses = [...addresses];
                                                        newAddresses.splice(index, 1);
                                                        setAddresses(newAddresses);
                                                    }}><Minus size={14} /></button>
                                                ) : (
                                                    <button type="button" className='form-box alt' title='Add another address' onClick={addAddress}><Plus size={14} /></button>
                                                )}
                                            </div>
                                            {showNoteFor === `address-${index}` && (
                                                <div className='form-group nm full contact-note-editor'>
                                                    <label className='subtext' htmlFor={`edit-contact-address-note-${index}`}>Address notes</label>
                                                    <textarea
                                                        id={`edit-contact-address-note-${index}`}
                                                        ref={noteTextareaRef}
                                                        placeholder="Address notes..."
                                                        value={address.note || ""}
                                                        onChange={(e) => {
                                                            const updated = [...addresses];
                                                            updated[index].note = e.target.value;
                                                            setAddresses(updated);
                                                        }}
                                                        onBlur={() => setShowNoteFor(null)}
                                                        rows="3"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : currentPage === 1 ? (
                            <>
                                {!contactInformation.is_company && (
                                    <>
                                        <div className='sub-title'>
                                            <h4>Personal Information</h4>
                                        </div>
                                        <div className='contact-name-container'>
                                            <div className='contact-name-info'>
                                                <div className="form-group nm gender">
                                                    <label className='subtext' htmlFor="gender">Gender</label>
                                                    <select className='default-select' id="gender" value={contactInformation.gender} onChange={(e) => setContactInformation({ ...contactInformation, gender: e.target.value })}>
                                                        <option value="" disabled>Select Gender</option>
                                                        <option value="unknown">Unknown</option>
                                                        <option value="male">Male</option>
                                                        <option value="female">Female</option>
                                                        <option value="other">Gender Diverse</option>
                                                    </select>
                                                </div>
                                                <div className="form-group nm pronoun">
                                                    <label className='subtext' htmlFor="pronouns">Pronouns</label>
                                                    <select className='default-select' id="pronouns" value={contactInformation.pronouns} onChange={(e) => setContactInformation({ ...contactInformation, pronouns: e.target.value })}>
                                                        <option value="" disabled>Select Pronouns</option>
                                                        <option value="he">He/Him</option>
                                                        <option value="she">She/Her</option>
                                                        <option value="they">They/Them</option>
                                                    </select>
                                                </div>
                                                <div className="form-group nm language">
                                                    <label className='subtext' htmlFor="language">Language Spoken</label>
                                                    <input type="text" id="language" value={contactInformation.language} onChange={(e) => setContactInformation({ ...contactInformation, language: e.target.value })} />
                                                </div>
                                                <div className="form-group nm emergency-contact">
                                                    <label className='subtext' htmlFor="nickname">Emergency Contact</label>
                                                    <Contact 
                                                        selectedContact={contactInformation.emergencyContact}
                                                        setSelectedContact={(contact) => setContactInformation({ ...contactInformation, emergencyContact: contact?.id || contact })}
                                                        onCreateNewContact={() => setCreateContact(true)}
                                                    />
                                                </div>
                                                <div className="form-group nm social-media">
                                                    <label className='subtext' htmlFor="prefix">Social Media Pages</label>
                                                    <div className='social-media-links'>
                                                        {Array.isArray(contactInformation?.socialMedia) && contactInformation.socialMedia.length > 0 ? (
                                                            contactInformation.socialMedia.map((link, index) => (
                                                                <div key={index} className='social-media-input-container'>
                                                                    <Text
                                                                        type='link'
                                                                        value={link}
                                                                        onChange={(value) => {
                                                                            const newSocialMedia = [...contactInformation.socialMedia];
                                                                            newSocialMedia[index] = value;
                                                                            setContactInformation({
                                                                                ...contactInformation,
                                                                                socialMedia: newSocialMedia,
                                                                            });
                                                                        }}
                                                                    />
                                                                    {index > 0 ? (
                                                                        <button 
                                                                            className='form-box alt' 
                                                                            title='Remove this social media link' 
                                                                            onClick={() => {
                                                                                const newSocialMedia = [...contactInformation.socialMedia];
                                                                                newSocialMedia.splice(index, 1);
                                                                                setContactInformation({
                                                                                    ...contactInformation,
                                                                                    socialMedia: newSocialMedia,
                                                                                });
                                                                            }}
                                                                        >
                                                                            <Minus size={14} />
                                                                        </button>
                                                                    ) : (
                                                                        <div 
                                                                            className='form-box alt' 
                                                                            title='Add another social media link' 
                                                                            onClick={() => {
                                                                                setContactInformation({
                                                                                    ...contactInformation,
                                                                                    socialMedia: [...contactInformation.socialMedia, ''],
                                                                                });
                                                                            }}
                                                                        >
                                                                            <Plus size={14} />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className='social-media-input-container'>
                                                                <Text
                                                                    type='link'
                                                                    value=""
                                                                    onChange={(value) => {
                                                                        setContactInformation({
                                                                            ...contactInformation,
                                                                            socialMedia: [value],
                                                                        });
                                                                    }}
                                                                />
                                                                <div 
                                                                    className='form-box alt' 
                                                                    title='Add another social media link' 
                                                                    onClick={() => {
                                                                        setContactInformation({
                                                                            ...contactInformation,
                                                                            socialMedia: ['', ''],
                                                                        });
                                                                    }}
                                                                >
                                                                    <Plus size={14} />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="form-group nm ssn name-fg">
                                                    <label className='subtext' htmlFor="ssn">Social Security Number</label>
                                                    <input type="text" id="ssn" value={contactInformation.ssn} onChange={(e) => setContactInformation({ ...contactInformation, ssn: e.target.value })} />
                                                </div>
                                                <div className="form-group nm dl name-fg">
                                                    <label className='subtext' htmlFor="dl">Driver's License Number</label>
                                                    <input type="text" id="dl" value={contactInformation.dl} onChange={(e) => setContactInformation({ ...contactInformation, dl: e.target.value })} />
                                                </div>
                                                <div className="form-group nm dlp name-fg">
                                                    <label className='subtext' htmlFor="ssn">Driver's License/ID Card (Front and Back)</label>
                                                    <MultiFile
                                                        id="dlp"
                                                        value={contactInformation.dlp}
                                                        onChange={(files) => setContactInformation({ ...contactInformation, dlp: files })}
                                                    />
                                                </div>
                                                <div className="form-group nm education name-fg">
                                                    <label className='subtext' htmlFor="education">Education</label>
                                                    <textarea id="education" value={contactInformation.education} onChange={(e) => setContactInformation({ ...contactInformation, education: e.target.value })} />
                                                </div>
                                            </div>
                                            <div className='divider horizontal'></div>
                                        </div>
                                    </>
                                )}
                                <div className='divider horizontal'></div>
                                <ContactFieldPresets
                                    contactId={contactInformation.id}
                                    presets={contactPresets}
                                    setPresets={setContactPresets}
                                    setFieldMap={setPresetFieldMap}
                                />
                                <div className='divider horizontal'></div>
                                <div className='sub-title'>
                                    <h4>Additional Information</h4>
                                </div>
                                <div className='family-information'>
                                    <div className='form-group nm additional-notes'>
                                        <label className='subtext' htmlFor="additional_notes">Additional Notes</label>
                                        <textarea id="additional_notes" value={contactInformation.additional_notes} onChange={(e) => setContactInformation({ ...contactInformation, additional_notes: e.target.value })} />
                                    </div>
                                </div>
                            </>
                        ) : currentPage === 2 ? (
                            <>
                                <div className='sub-title'>
                                    <h4>Associated Cases</h4>
                                </div>
                                <div className="case-section">
                                    {relatedCases.length > 0 ? (
                                        <div className="linked-cases-section related-cases">
                                            <div className="case-list">
                                                {relatedCases.map((caseItem, index) => {
                                                    const contactDisplay = contactInformation?.profile_picture
                                                        ? <img src={`https://api.casedb.co/${contactInformation.profile_picture}`} alt="Profile" />
                                                        : (contactInformation?.first_name?.trim().charAt(0) || '') + (contactInformation?.last_name?.trim().charAt(0) || '') || "DB";
                                                    
                                                    const className = getContactInitialsClassName('mini-case-initials', caseItem.id);

                                                    return (
                                                        <EntityHoverPreview
                                                            as="div"
                                                            entityType="case"
                                                            entityId={caseItem.id}
                                                            className="mini-case-summary"
                                                            key={index}
                                                        >
                                                            <div className={className} onClick={() => navigate(`/case/${caseItem.id}`)}>
                                                                {contactDisplay}
                                                            </div>
                                                            <div className="mini-case-meta" onClick={() => navigate(`/case/${caseItem.id}`)}>
                                                                <div className="mini-case-name">
                                                                    {caseItem.name}
                                                                    <span className='subtext'>
                                                                        ({caseItem.id})
                                                                    </span>
                                                                </div>
                                                                <div className='mini-case-info subtext'>
                                                                    {caseItem.field_name && (
                                                                        <span>
                                                                            Found in: <span className="field-connection">{caseItem.field_name}</span>
                                                                        </span>
                                                                    )}
                                                                    <div className='mini-case-vitals' style={{ display: "flex", flexWrap: 'wrap', alignItems: "center", justifyContent: "flex-start", gap: "2.5px" }}>
                                                                        {caseItem.vitals?.first_primary && (
                                                                            <>
                                                                                <span>{caseItem?.vitals?.first_primary?.[0]}{caseItem?.vitals?.first_primary?.[1] ? ` (@${caseItem?.vitals?.first_primary?.[1]})` : null}</span>
                                                                                {caseItem.vitals?.phase || caseItem.vitals?.incident_date ? "•" : null}
                                                                            </>
                                                                        )}
                                                                        {caseItem.vitals?.phase && (
                                                                            <>
                                                                                <span>{caseItem.vitals.phase}</span>
                                                                                {caseItem.vitals?.incident_date ? "•" : null}
                                                                            </>
                                                                        )}
                                                                        {caseItem.vitals?.incident_date && (
                                                                            <>
                                                                                <span>{formatDate(caseItem.vitals.incident_date)[0]}</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </EntityHoverPreview>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="linked-cases-section related-cases">
                                            <div className="no-cases">
                                                <p className='subtext'>No associated cases found for this contact.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : currentPage === 3 ? (
                            <>
                                <div className='sub-title'>
                                    <h4>Weather Events</h4>
                                    <div className='toc-header-actions'>
                                        <div className='reload small-text' onClick={() => fetchContactWeatherEvents(false)}>
                                            {showWeatherRefreshCheck ? (
                                                <Check size={14} />
                                            ) : (
                                                <RefreshCw size={14} className={isWeatherLoading ? 'spinner small' : undefined} />
                                            )}
                                            {weatherEvents.length > 0 ? 'Reload' : 'Loading Events'}
                                        </div>
                                    </div>
                                </div>
                                <div className="case-section case-weather-events" style={{ width: '100%' }}>
                                    <div className='form-group filter-group nm full' style={{ width: '100%', maxWidth: '100%' }}>
                                        <label className='subtext' htmlFor='weather-address'>Address</label>
                                        <div className="select-wrapper" style={{ width: '100%' }}>
                                            <select
                                                className='filter-select'
                                                id='weather-address'
                                                style={{ width: '100%' }}
                                                value={selectedWeatherAddressIndex}
                                                onChange={(e) => {
                                                    setSelectedWeatherAddressIndex(e.target.value);
                                                    setWeatherEvents([]);
                                                    setWeatherError('');
                                                    setWeatherAddress(null);
                                                    setWeatherCoordinates(null);
                                                    fetchContactWeatherEvents(false, e.target.value);
                                                }}
                                                disabled={getWeatherAddressOptions().length === 0 || isWeatherLoading}
                                            >
                                                {getWeatherAddressOptions().length === 0 ? (
                                                    <option value=''>No complete addresses available</option>
                                                ) : (
                                                    getWeatherAddressOptions().map(({ index, label, address }) => (
                                                        <option key={index} value={String(index)}>
                                                            {address.type ? `${address.type.charAt(0).toUpperCase()}${address.type.slice(1)} - ` : ''}{label}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                            <ChevronDown size={16} className="select-chevron" />
                                        </div>
                                    </div>
                                    {weatherError && (
                                        <div className='subtext' style={{ color: 'var(--destroy)', width: '100%' }}>
                                            {weatherError}
                                        </div>
                                    )}
                                    <table className='weather-events'>
                                        <thead>
                                            <tr className='small-text'>
                                                <th>Date</th>
                                                <th>Event Type</th>
                                                <th>Meteorologist Magnitude</th>
                                                <th>Algorithm Magnitude</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {weatherEvents.length > 0 ? weatherEvents.map((event, index) => (
                                                <tr key={event.id || index} className='subtext' style={{ backgroundColor: 'var(--background-color)' }}>
                                                    <td>{formatWeatherEventDate(event.eventDate)}</td>
                                                    <td>{getWeatherEventType(event)}</td>
                                                    <td>{event.maxMeteorologistWindSpeedMPH ? `${event.maxMeteorologistWindSpeedMPH} MPH` : '-'}</td>
                                                    <td>{(event.maxMeteorologistHailSize || event.maxAlgorithmHailSize) ? `${event.maxAlgorithmHailSize || event.maxMeteorologistHailSize} in` : '-'}</td>
                                                </tr>
                                            )) : (
                                                <tr className='subtext'>
                                                    <td style={{ width: '100%' }} colSpan={4}>
                                                        {isWeatherLoading ? 'Loading weather events...' : 'Select an address and load weather events.'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {getWeatherMapAddress() && (
                                        <div style={{ width: '100%', marginTop: '16px' }}>
                                            <div className='subtext' style={{ marginBottom: '6px' }}>
                                                Map{weatherCoordinates ? ` (${weatherCoordinates.latitude}, ${weatherCoordinates.longitude})` : ''}
                                            </div>
                                            <iframe
                                                title='Weather Events Address Map'
                                                width='100%'
                                                height='300'
                                                style={{ border: 0, borderRadius: '12px' }}
                                                loading='lazy'
                                                allowFullScreen
                                                referrerPolicy='no-referrer-when-downgrade'
                                                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBIJcqTSLaIqtZWQSTPHGJ_IvX1gkn2q08&q=${encodeURIComponent(getWeatherMapAddress())}`}
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                            </>
                        )}
                    </form>
                ) : (
                    <div className='middle'>
                        Deleting this contact will remove all associated data. Are you sure you want to delete this contact?
                    </div>
                )}
            </div>
            {showNatureModal && (
                <Modal
                    title="Contact Nature"
                    onClose={() => setShowNatureModal(false)}
                    footer={
                        <div className='modal-footer-actions'>
                            <button
                                onClick={() => setShowNatureModal(false)}
                                className="action alt"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveNature}
                                className="action"
                                disabled={contactInformation.nature === null}
                            >
                                Save
                            </button>
                        </div>
                    }
                >
                    <div className="modal-content-wrapper">
                        <div className='smiles-container'>
                            <div
                                onClick={() => setContactInformation({ ...contactInformation, nature: 1 })}
                                className={`smile-container ${contactInformation.nature === 1 ? 'selected' : ''}`}
                            >
                                <Smile size={30} className="contact-nature-icon well-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Well Natured</div>
                            </div>
                            <div
                                onClick={() => setContactInformation({ ...contactInformation, nature: 2 })}
                                className={`smile-container ${contactInformation.nature === 2 ? 'selected' : ''}`}
                            >
                                <Meh size={30} className="contact-nature-icon neutral-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Neutral</div>
                            </div>
                            <div
                                onClick={() => setContactInformation({ ...contactInformation, nature: 3 })}
                                className={`smile-container ${contactInformation.nature === 3 ? 'selected' : ''}`}
                            >
                                <Frown size={30} className="contact-nature-icon ill-natured" />
                                <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>Ill Natured</div>
                            </div>
                        </div>

                        <div className='form-group full' style={{ maxWidth: '100%', width: '100%' }}>
                            <label className="subtext">
                                Nature Description
                            </label>
                            <textarea
                                value={contactInformation.nature_description}
                                onChange={(e) => setContactInformation({ ...contactInformation, nature_description: e.target.value })}
                                placeholder="Add notes about this contact's nature..."
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </Modal>
    );
};
